'use client';

import cn from 'clsx';
import { format } from 'date-fns';
import { HTMLAttributes } from 'react';
import { DayPicker, MonthProps } from 'react-day-picker';

import { BoxButton } from '../../button';

import { calendarClassNames, calendarFormatters, DAYS } from './constants/calendar';
import { useCalendarDates } from './hooks/useCalendarDates';
import { CalendarProps, ScrollCalendarType } from './type/BaseCalendarType';
import { isSingleMode } from './utils/isSingleMode';

export function ScrollableCalendar(props: CalendarProps & ScrollCalendarType) {
  const { className, baseDate, disabled, baseCalendarAmount, numbersOfMonths, disableTargetDate, isInnerDrawer } = props;

  const { numberOfMonths, renderedDate, isDisabledBoundary, isDisabledRange, loadPrevCalendar, loadNextCalendar } = useCalendarDates({
    baseDate,
    baseCalendarAmount,
    disabled
  });

  const getDisabledDay = (date: Date) => {
    if (!disabled) {
      return false;
    } else if (isDisabledRange(disabled)) {
      return date >= disabled.from && date <= disabled.to;
    } else if (isDisabledBoundary(disabled)) {
      return date < disabled.before || date > disabled.after;
    }
    return false;
  };

  const isDisabledTargetDate = (date: Date) => {
    return disableTargetDate?.some((disableDate) => format(disableDate, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')) ?? false;
  };

  const handleDayClick = (date: Date) => {
    if (isDisabledTargetDate(date)) return;

    if (isSingleMode(props)) {
      props.onSelectDate(date);
    } else {
      const currentDates = props.selectedDate ?? [];
      const isAlreadySelected = currentDates.some((d) => d.getTime() === date.getTime());
      if (isAlreadySelected) {
        props.onSelectDate(currentDates.filter((d) => d.getTime() !== date.getTime()));
      } else {
        props.onSelectDate([...currentDates, date]);
      }
    }
  };

  const DayComponent = ({ day: { date, displayMonth }, modifiers }: any) => {
    const currentMonth = date.getMonth();
    const displayedMonth = displayMonth.getMonth();
    const isDisplayedDay = currentMonth === displayedMonth;
    const isSelected = modifiers.selected;
    const isDisabled = getDisabledDay(date);
    const isTargetDisabled = isDisabledTargetDate(date);

    if (!isDisplayedDay) {
      return <td className={'flex-1 flex justify-center items-center py-tiny-large'} />;
    }

    return (
      <td
        onClick={() => handleDayClick(date)}
        className={cn(
          'group flex-1 flex justify-center items-center py-tiny-large text-neutral-foreground-main body-medium-semibold',
          { 'cursor-pointer': !isSelected },
          { 'pointer-events-none !text-neutral-foreground-disabled': isDisabled || isTargetDisabled },
          { '!text-bzc-red-600': date.getDay() === 0 }
        )}
      >
        {isSelected && <div className="absolute z-0 flex h-8 w-8 items-center justify-center rounded-full bg-primary-background-main" />}
        {isTargetDisabled && <div className="absolute z-0 flex h-8 w-8 items-center justify-center rounded-full bg-neutral-background-disabled-main" />}
        <div
          className={cn('absolute flex h-8 w-8 items-center justify-center rounded-full', {
            'group-hover:bg-neutral-background-hovered&pressed': !isSelected
          })}
        />
        <span className={cn('z-10', { 'text-neutral-foreground-inverse-main': isSelected })}>{date.getDate()}</span>
      </td>
    );
  };

  const commonProps = {
    month: renderedDate,
    showOutsideDays: false,
    numberOfMonths: numberOfMonths,
    hideWeekdays: true,
    hideNavigation: true,
    components: {
      Month: (props: MonthProps) => {
        const {
          children,
          className,
          style,
          calendarMonth: { date }
        } = props;

        return <div id={format(date, 'yyyyMMdd')} children={children} className={className} style={style} />;
      },
      CaptionLabel: (captionProps: HTMLAttributes<HTMLSpanElement>) => (
        <div className={'w-full'}>
          <CalendarLabel captionProps={captionProps} />
        </div>
      ),
      Day: DayComponent
    },
    className: cn(className, 'pb-tiny-medium'),
    classNames: calendarClassNames,
    formatters: calendarFormatters
  };

  return (
    <div>
      <div
        className={`flex pt-normal-small gap-tiny-large pb-tiny-medium sticky ${isInnerDrawer ? 'top-0' : 'top_safe_env_navi'} items-center justify-between bg-neutral-background-main z-30`}
      >
        {DAYS.map((day) => {
          return (
            <span key={day} className={'text-neutral-foreground-lowest caption-xsmall-regular p-tiny-medium flex-1 text-center'}>
              {day}
            </span>
          );
        })}
        <div
          className={'absolute top-[100%] h-8 w-full'}
          style={{ background: 'linear-gradient(180deg, #FFF 0%, rgba(255, 255, 255, 0.75) 50%, rgba(255, 255, 255, 0.00) 100%)' }}
        />
      </div>
      <div className={'w-full h-8'} />
      <BoxButton
        className={'w-full mb-normal-large'}
        variant={'tonal-secondary'}
        onClick={() => {
          loadPrevCalendar(numbersOfMonths);
        }}
      >
        이전 월 보기
      </BoxButton>
      {isSingleMode(props) ? (
        <DayPicker
          mode="single"
          selected={props.selectedDate}
          onSelect={(date) => {
            if (date) props.onSelectDate(date);
          }}
          {...commonProps}
        />
      ) : (
        <DayPicker
          mode="multiple"
          selected={props.selectedDate}
          onSelect={(dates) => {
            if (dates) props.onSelectDate(dates);
          }}
          {...commonProps}
        />
      )}
      <div className={'w-full flex justify-center'}>
        <BoxButton
          className={'w-full mt-normal-large'}
          variant={'tonal-secondary'}
          onClick={() => {
            loadNextCalendar(numbersOfMonths);
            setTimeout(() => {
              window.document.body.scrollIntoView({ block: 'end', behavior: 'smooth' });
            }, 0);
          }}
        >
          다음 월 보기
        </BoxButton>
      </div>
    </div>
  );
}

const CalendarLabel = ({ captionProps }: { captionProps: HTMLAttributes<HTMLSpanElement> }) => {
  return (
    <div className="w-full mb-tiny-large p-tiny-large">
      <p className="body-large-semibold flex-1 text-neutral-foreground-main">
        <span {...captionProps} />
      </p>
    </div>
  );
};
